import type { DailyRewardAnalyticsRow } from "@ocs/shared-types";

const USDT_DECIMALS = 18;
const USDT_SCALE = 10n ** BigInt(USDT_DECIMALS);

/** Amounts are never rounded through Number; Number is reserved for chart geometry. */
export function usdtUnits(value: string): bigint {
  const match = /^(-?)(\d+)(?:\.(\d{1,18}))?$/.exec(value);
  if (!match) throw new Error(`Invalid USDT decimal: ${value}`);
  const units = BigInt(match[2]!) * USDT_SCALE + BigInt((match[3] ?? "").padEnd(USDT_DECIMALS, "0"));
  return match[1] ? -units : units;
}

export function unitsUsdt(value: bigint): string {
  const negative = value < 0n;
  const absolute = negative ? -value : value;
  const fraction = (absolute % USDT_SCALE).toString().padStart(USDT_DECIMALS, "0").replace(/0+$/, "");
  return `${negative ? "-" : ""}${absolute / USDT_SCALE}${fraction ? `.${fraction}` : ""}`;
}

export function exactUsdt(value: string | null | undefined, signed = false): string {
  if (value == null) return "—";
  const normalized = unitsUsdt(usdtUnits(value));
  const negative = normalized.startsWith("-");
  const [whole, fraction = ""] = (negative ? normalized.slice(1) : normalized).split(".");
  return `${negative ? "−" : signed && usdtUnits(value) > 0n ? "+" : ""}${whole!.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${fraction.padEnd(2, "0")}`;
}

export function totalUsdt(values: string[]): string {
  return unitsUsdt(values.reduce((sum, value) => sum + usdtUnits(value), 0n));
}

/** Cashflow comparison only: a negative result is not an outstanding reward liability. */
export function receivedMinusTheoretical(theoretical: string | null, received: string | null): string | null {
  if (theoretical == null || received == null) return null;
  return unitsUsdt(usdtUnits(received) - usdtUnits(theoretical));
}

export function chartAmount(value: string | null): number | null {
  if (value == null) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
}

export function comparableTotals(rows: DailyRewardAnalyticsRow[]) {
  const comparable = rows.filter((row) => row.status === "ready"
    && row.settlementStatus === "ready" && row.indexerStatus === "ready"
    && row.theoretical.amountUsdt != null && row.received.netUsdt != null
    && row.received.grossUsdt != null && row.received.feeUsdt != null);
  return {
    readyDays: comparable.length,
    pendingDays: rows.length - comparable.length,
    theoreticalUsdt: totalUsdt(comparable.map((row) => row.theoretical.amountUsdt!)),
    receivedNetUsdt: totalUsdt(comparable.map((row) => row.received.netUsdt!)),
    receivedGrossUsdt: totalUsdt(comparable.map((row) => row.received.grossUsdt!)),
    receivedFeeUsdt: totalUsdt(comparable.map((row) => row.received.feeUsdt!))
  };
}

export function beijingTimestamp(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
  }).format(parsed);
}

const statisticReasons: Record<string, string> = {
  LAST_STATISTICS_REFRESH_FAILED: "最近一次统计刷新失败，当前保留上次快照。",
  AWAITING_FIRST_STATISTICS_RUN: "等待首次统计任务生成该日快照。",
  DAILY_SETTLEMENT_INCOMPLETE: "当日日结尚未完整完成。",
  MARKET_PAYOUT_INDEXER_BEHIND_OR_UNAVAILABLE: "Market 领取索引待追平或暂不可用。",
  NODE_PAYOUT_INDEXER_BEHIND_OR_UNAVAILABLE: "原节点领取索引待追平或暂不可用。",
  MISSING_VERIFIED_PAYOUT_TIME_OR_AMOUNTS: "部分领取记录缺少已核验的到账时间或金额。",
  QUARANTINED_CHAIN_EVENTS: "存在尚未完成核验的链上事件。",
  MARKET_SETTLEMENT_PENDING: "Market 当日日结尚未完成。",
  MARKET_SETTLEMENT_FAILED: "Market 当日日结失败，等待重新核验。",
  MARKET_PAYOUT_CURSOR_BEHIND: "Market 领取索引尚未覆盖该日结束时刻。",
  NODE_PAYOUT_CURSOR_BEHIND: "原节点领取索引尚未覆盖该日结束时刻。"
};

export function statisticsReason(reason: string): string {
  return statisticReasons[reason] ?? "统计资料待核验。";
}
